import {
  sanitizeAnalyticsProperties,
  type AnalyticsProperties,
} from '@/analytics/privacy';

export type AnalyticsEventProperties = AnalyticsProperties;

type AnalyticsIdentity = {
  userId: string;
  role?: string;
  contactHash?: string;
};

type AnalyticsAdapter = {
  capture: (eventName: string, properties?: AnalyticsProperties) => void;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void;
  reset: () => void;
  opt_in_capturing?: () => void;
  opt_out_capturing?: () => void;
};

type AnalyticsStatus = {
  enabled: boolean;
  initialized: boolean;
  optedOut: boolean;
  disabledReason?: 'missing_env' | 'not_initialized' | 'opted_out' | 'unavailable';
};

const analyticsOptOutStorageKey = 'rodeonails.analytics.optOut';

function readStoredOptOut() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(analyticsOptOutStorageKey) === 'true';
}

function writeStoredOptOut(optedOut: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(analyticsOptOutStorageKey, String(optedOut));
}

export function createAnalyticsClient() {
  let adapter: AnalyticsAdapter | null = null;
  let initialized = false;
  let disabledReason: AnalyticsStatus['disabledReason'] = 'not_initialized';
  let optedOut = readStoredOptOut();

  const canCapture = () => Boolean(adapter && initialized && !optedOut);

  return {
    configure(nextAdapter: AnalyticsAdapter | null, options?: { disabledReason?: AnalyticsStatus['disabledReason'] }) {
      adapter = nextAdapter;
      initialized = Boolean(nextAdapter);
      disabledReason = nextAdapter ? undefined : options?.disabledReason ?? 'missing_env';

      if (nextAdapter && optedOut) {
        nextAdapter.opt_out_capturing?.();
      }
    },

    getStatus(): AnalyticsStatus {
      if (optedOut) {
        return {
          enabled: false,
          initialized,
          optedOut,
          disabledReason: 'opted_out',
        };
      }

      return {
        enabled: canCapture(),
        initialized,
        optedOut,
        disabledReason: canCapture() ? undefined : disabledReason,
      };
    },

    track(eventName: string, properties: Record<string, unknown> = {}) {
      if (!canCapture()) {
        return;
      }

      try {
        adapter?.capture(eventName, sanitizeAnalyticsProperties(properties));
      } catch {
        // Analytics must never block salon workflows.
      }
    },

    identify(identity: AnalyticsIdentity, properties: Record<string, unknown> = {}) {
      if (!canCapture()) {
        return;
      }

      try {
        adapter?.identify(
          identity.userId,
          sanitizeAnalyticsProperties({
            role: identity.role,
            contact_hash: identity.contactHash,
            ...properties,
          }),
        );
      } catch {
        // Identification is best-effort only.
      }
    },

    reset() {
      try {
        adapter?.reset();
      } catch {
        // Reset is best-effort only.
      }
    },

    setOptOut(nextOptedOut: boolean) {
      optedOut = nextOptedOut;
      writeStoredOptOut(nextOptedOut);

      try {
        if (nextOptedOut) {
          adapter?.opt_out_capturing?.();
          return;
        }

        adapter?.opt_in_capturing?.();
      } catch {
        // Preference persistence still succeeded locally.
      }
    },

    isOptedOut() {
      return optedOut;
    },
  };
}

export const analytics = createAnalyticsClient();
